import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import PageHeader from '../components/PageHeader';
import IngredienteForm from '../components/IngredienteForm';
import {
  ChefHat,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Archive,
  ArchiveRestore,
  Clock,
  Users,
  Package,
  AlertTriangle,
  X,
  Save,
  User,
  Trash2,
  ShoppingCart,
} from 'lucide-react';
import type {
  Receta,
  EstadoReceta,
  NuevaRecetaFormData,
} from '../types/recetas';
import type { FiltrosRecetas } from '../services/recetasAPI';
import { recetasAPI } from '../services/recetasAPI';
import { ESTADOS_RECETA } from '../types/recetas';
import { TipoPlato } from '../types/minutasNuevas';
import type { Ingrediente } from '../types/ingredientes';
import {
  useRecetas,
  useReceta,
  useRecetasEstadisticas,
  useRecetasManager,
  useAgregarIngrediente,
  useRemoverIngrediente,
  useActualizarIngrediente,
  useIncrementarCantidadIngrediente,
} from '../hooks/useRecetas';
import {
  useCreateIngrediente,
  useIngredientes,
  useReactivarIngrediente,
} from '../hooks/useIngredientes';
import { useTagsReceta, TagRecetaAPI } from '../hooks/useTagsReceta';
import { adaptarRecetaAPIToReceta } from '../utils/recetasAdapter';
import type {
  ActualizarIngredienteRequest,
  AgregarIngredienteRequest,
} from '../services/recetasAPI';
import IngredienteFormSimple from '../components/IngredienteFormSimple';
import RecetaErrorModal from '../components/RecetaErrorModal';
import ConfirmModal from '../components/ConfirmModal';
import IngredienteSimilarModal from '../components/IngredienteSimilarModal';

// Función utilitaria para normalizar unidades de medida
const getUnidadMedidaText = (unidadMedida: any): string => {
  if (typeof unidadMedida === 'string') {
    // Normalizar unidades de medida a abreviaturas consistentes
    const unidad = unidadMedida.toLowerCase();
    switch (unidad) {
      case 'kilogramos':
      case 'kilogramo':
      case 'kilo':
      case 'kilos':
        return 'kg';
      case 'gramos':
      case 'gramo':
        return 'g';
      case 'litros':
      case 'litro':
        return 'l';
      case 'mililitros':
      case 'mililitro':
        return 'ml';
      case 'unidades':
      case 'unidad':
        return 'ud';
      case 'cucharadas':
      case 'cucharada':
        return 'cdas';
      case 'cucharaditas':
      case 'cucharadita':
        return 'cditas';
      case 'tazas':
      case 'taza':
        return 'tzs';
      case 'piezas':
      case 'pieza':
        return 'pzs';
      default:
        return unidad;
    }
  }
  return unidadMedida?.abreviacion || unidadMedida?.nombre || 'ud';
};

// Función utilitaria para formatear moneda en pesos chilenos
const formatearMoneda = (valor: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(valor);
};

const Recetas: React.FC = () => {
  const { user } = useAuth();
  const { success: toastSuccess, error: toastError } = useToast();

  // Helper: extrae el mensaje útil de un error de axios/mutación, con
  // fallback al string genérico. Centralizado acá porque crear/actualizar/
  // eliminar receta comparten el mismo patrón de error silencioso.
  const extraerMensajeError = (err: any, fallback: string): string => {
    const data = err?.response?.data;
    return (
      data?.message || data?.error || err?.message || fallback
    );
  };

  // Estados de filtros y UI
  const initialFiltros: FiltrosRecetas = {
    page: 1,
    limit: 10,
  };
  const [filtros, setFiltros] = useState(initialFiltros);
  const [busquedaLocal, setBusquedaLocal] = useState('');
  const [showFiltros, setShowFiltros] = useState(false);
  const [showNuevaRecetaModal, setShowNuevaRecetaModal] = useState(false);
  const [showDetallesModal, setShowDetallesModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [recetaSeleccionada, setRecetaSeleccionada] = useState<Receta | null>(
    null
  );
  const [tiposPlatos, setTiposPlatos] = useState<TipoPlato[]>([]);
  const [_cargandoTiposPlatos, setCargandoTiposPlatos] = useState(false);

  // Cargar ingredientes desde la API
  const { data: ingredientesResponse } = useIngredientes({
    limit: 1000, // Obtener muchos ingredientes para las sugerencias
    activo: true, // Solo ingredientes activos
  });
  const ingredientes = ingredientesResponse?.ingredientes || [];

  // Hooks de React Query para recetas
  const { data: recetasResponse, isLoading: loading } = useRecetas(filtros);

  // Hook para estadísticas (todas las recetas sin paginación)
  const { data: todasLasRecetas = [] } = useRecetasEstadisticas();

  // Debug: log de filtros y resultados
  console.log('🔍 Recetas.tsx - Filtros actuales:', filtros);
  console.log('📊 Recetas.tsx - Respuesta de API:', recetasResponse);

  // Manager para operaciones CRUD
  const { crear: crearReceta, actualizar: actualizarReceta } =
    useRecetasManager();

  // Catálogo de tags (vegetariano, etc.) para los modales de crear/editar.
  const { data: tagsCatalogo = [] } = useTagsReceta();

  // Adaptar datos de la API al formato del frontend
  const recetas: Receta[] =
    recetasResponse?.data?.map(adaptarRecetaAPIToReceta) || [];

  // No hacemos filtrado local, usamos solo el del backend
  const recetasFiltradas = recetas;

  const puedeEditar = user?.rol === 'administrador' || user?.rol === 'chef';

  const obtenerEstadoInfo = (estado: EstadoReceta) => {
    return ESTADOS_RECETA.find((e) => e.value === estado) || ESTADOS_RECETA[0];
  };

  // Cargar tipos de platos dinámicamente al montar el componente
  React.useEffect(() => {
    const cargarTiposPlatos = async () => {
      setCargandoTiposPlatos(true);
      try {
        console.log(
          '🔄 Cargando tipos de platos desde recetasAPI.obtenerTiposPlato()...'
        );
        // Usar el servicio recetasAPI que tiene la configuración correcta
        const tiposData = await recetasAPI.obtenerTiposPlato();
        // Convertir el tipo activo de number a boolean
        const tiposConvertidos = tiposData.map(tipo => ({
          ...tipo,
          activo: Boolean(tipo.activo)
        }));
        setTiposPlatos(tiposConvertidos);
        console.log(
          '✅ Tipos de platos cargados:',
          tiposData.length,
          tiposData
        );
      } catch (error) {
        console.error('❌ Error al cargar tipos de platos:', error);
      } finally {
        setCargandoTiposPlatos(false);
      }
    };

    cargarTiposPlatos();
  }, []);

  const cambiarEstadoReceta = (id: string, nuevoEstado: EstadoReceta) => {
    actualizarReceta({
      id: id,
      data: { estado: nuevoEstado },
    });
  };

  // Fix tanda1 H3 (2026-06-30): el botón "🗑️ / Archivar receta" antes
  // archivaba al instante sin confirmación → riesgo alto de archivado
  // accidental. Ahora abre un ConfirmModal igual al patrón ya usado para
  // eliminar ingrediente de receta (ver `confirmarEliminarIngrediente`).
  // El archivado real ocurre en `confirmarArchivarReceta` (abajo).
  const [archivarConfirm, setArchivarConfirm] = useState<{
    open: boolean;
    recetaId: string;
    recetaNombre: string;
  }>({ open: false, recetaId: '', recetaNombre: '' });

  const archivarReceta = (receta: { id: string; nombre: string }) => {
    setArchivarConfirm({
      open: true,
      recetaId: receta.id,
      recetaNombre: receta.nombre,
    });
  };

  const confirmarArchivarReceta = () => {
    if (archivarConfirm.recetaId) {
      cambiarEstadoReceta(archivarConfirm.recetaId, 'archivada');
    }
    setArchivarConfirm({ open: false, recetaId: '', recetaNombre: '' });
  };

  const restaurarReceta = (id: string) => {
    cambiarEstadoReceta(id, 'disponible');
  };

  // Funciones de paginación
  // Debouncing para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setFiltros((prev) => ({
        ...prev,
        busqueda: busquedaLocal || undefined,
        page: 1, // Reset a página 1 solo cuando la búsqueda cambia realmente
      }));
    }, 300); // 300ms de delay

    return () => clearTimeout(timer);
  }, [busquedaLocal]);

  // Función para cambiar página (igual que en ingredientes)
  const handlePageChange = (newPage: number) => {
    const totalPages = recetasResponse?.pagination?.totalPages || 0;
    if (newPage >= 1 && newPage <= totalPages) {
      setFiltros((prev) => ({ ...prev, page: newPage }));
    }
  };

  const abrirDetalles = (receta: Receta) => {
    setRecetaSeleccionada(receta);
    setShowDetallesModal(true);
  };

  const abrirEdicion = (receta: Receta) => {
    setRecetaSeleccionada(receta);
    setShowEditarModal(true);
  };

  const cerrarModales = () => {
    setShowDetallesModal(false);
    setShowEditarModal(false);
    setRecetaSeleccionada(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 relative"
      style={{
        backgroundImage: "url('/bg_dash.webp')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-white/85 dark:bg-gray-900/85"></div>

      <PageHeader
        title="Gestión de Recetas"
        helpSeccion="recetas"
        subtitle="Administrar recetas y control de inventario de ingredientes"
        actions={
          puedeEditar ? (
            <button
              onClick={() => setShowNuevaRecetaModal(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
            >
              <Plus className="h-5 w-5 mr-2" />
              Nueva Receta
            </button>
          ) : undefined
        }
      />

      {/* Contenido */}
      <div className="relative z-10 p-6">
        <div className="mb-8">
          {/* Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
              <div className="flex items-center">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                  <ChefHat className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Recetas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {todasLasRecetas.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
              <div className="flex items-center">
                <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
                  <Package className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Disponibles
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {
                      todasLasRecetas.filter((r) => r.estado === 'disponible')
                        .length
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
              <div className="flex items-center">
                <div className="bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-lg">
                  <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Borradores
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {
                      todasLasRecetas.filter((r) => r.estado === 'borrador')
                        .length
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
              <div className="flex items-center">
                <div className="bg-gray-100 dark:bg-gray-900/30 p-3 rounded-lg">
                  <Archive className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Archivadas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {
                      todasLasRecetas.filter((r) => r.estado === 'archivada')
                        .length
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros y Búsqueda */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 mb-6">
            <div className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                {/* Búsqueda */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar recetas..."
                    value={busquedaLocal}
                    onChange={(e) => setBusquedaLocal(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {/* Botón de filtros */}
                <button
                  onClick={() => setShowFiltros(!showFiltros)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filtros
                </button>
              </div>

              {/* Panel de filtros expandido */}
              {showFiltros && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tipo de Plato
                      </label>
                      <select
                        value={filtros.tipoPlato || ''}
                        onChange={(e) =>
                          setFiltros((prev) => ({
                            ...prev,
                            tipoPlato: e.target.value || undefined,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="">Todos los tipos</option>
                        {tiposPlatos.map((tipo) => (
                          <option key={tipo.id} value={tipo.nombre}>
                            {tipo.nombre}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Estado
                      </label>
                      <select
                        value={filtros.estado || ''}
                        onChange={(e) =>
                          setFiltros((prev) => ({
                            ...prev,
                            estado: e.target.value
                              ? (e.target.value as EstadoReceta)
                              : undefined,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="">Todos los estados</option>
                        {ESTADOS_RECETA.map((est) => (
                          <option key={est.value} value={est.value}>
                            {est.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-end">
                      <button
                        onClick={() => setFiltros({})}
                        className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors duration-200"
                      >
                        Limpiar Filtros
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lista de Recetas */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Receta
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Tipo de Plato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Porciones
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ingredientes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Costo Total
                    </th>
                    {puedeEditar && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Acciones
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {recetasFiltradas.map((receta) => {
                    const estadoInfo = obtenerEstadoInfo(receta.estado);

                    return (
                      <tr
                        key={receta.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {receta.nombre}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {receta.descripcion}
                            </div>
                            {receta.tags && receta.tags.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {receta.tags.map((t) => (
                                  <span
                                    key={t.id}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700"
                                  >
                                    {t.nombre}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {receta.tipoPlato || 'Normal'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Users className="h-4 w-4 text-gray-400 mr-1" />
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {receta.porciones}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {receta.ingredientes.length} ingredientes
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${estadoInfo.color}`}
                          >
                            {estadoInfo.label}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            $
                            {receta.costoTotal?.toLocaleString('es-CL', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }) ?? '0'}
                          </span>
                        </td>
                        {puedeEditar && (
                          <td className="px-6 py-4 text-right space-x-2">
                            <button
                              title="Ver detalles"
                              onClick={() => abrirDetalles(receta)}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              title="Editar receta"
                              onClick={() => abrirEdicion(receta)}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {receta.estado === 'archivada' ? (
                              <button
                                title="Restaurar receta"
                                onClick={() => restaurarReceta(receta.id)}
                                className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300"
                              >
                                <ArchiveRestore className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                title="Archivar receta"
                                onClick={() =>
                                  archivarReceta({
                                    id: receta.id,
                                    nombre: receta.nombre,
                                  })
                                }
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {recetasFiltradas.length === 0 && (
              <div className="text-center py-12">
                <ChefHat className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                  No hay recetas
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {Object.keys(filtros).length > 0
                    ? 'No se encontraron recetas con los filtros aplicados'
                    : 'Comienza creando tu primera receta'}
                </p>
                {puedeEditar && Object.keys(filtros).length === 0 && (
                  <div className="mt-6">
                    <button
                      onClick={() => setShowNuevaRecetaModal(true)}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Nueva Receta
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Paginación (igual que en ingredientes) */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {recetasResponse?.pagination?.total
                    ? `Mostrando ${(recetasResponse.pagination.page - 1) * recetasResponse.pagination.limit + 1} a ${Math.min(recetasResponse.pagination.page * recetasResponse.pagination.limit, recetasResponse.pagination.total)} de ${recetasResponse.pagination.total} recetas`
                    : 'No hay recetas para mostrar'}
                </div>

                {recetasResponse?.pagination &&
                  recetasResponse.pagination.totalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          handlePageChange(recetasResponse.pagination.page - 1)
                        }
                        disabled={recetasResponse.pagination.page <= 1}
                        className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>

                      {/* Botones de páginas */}
                      {Array.from(
                        {
                          length: Math.min(
                            5,
                            recetasResponse.pagination.totalPages
                          ),
                        },
                        (_, i) => {
                          const pageNum = i + 1;
                          const isCurrentPage =
                            pageNum === recetasResponse.pagination.page;

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-1 rounded text-sm ${
                                isCurrentPage
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}

                      {recetasResponse.pagination.totalPages > 5 &&
                        recetasResponse.pagination.page <
                          recetasResponse.pagination.totalPages - 2 && (
                          <>
                            <span className="text-gray-500">...</span>
                            <button
                              onClick={() =>
                                handlePageChange(
                                  recetasResponse.pagination.totalPages
                                )
                              }
                              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                            >
                              {recetasResponse.pagination.totalPages}
                            </button>
                          </>
                        )}

                      <button
                        onClick={() =>
                          handlePageChange(recetasResponse.pagination.page + 1)
                        }
                        disabled={
                          recetasResponse.pagination.page >=
                          recetasResponse.pagination.totalPages
                        }
                        className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal Nueva Receta */}
        {showNuevaRecetaModal && (
          <NuevaRecetaModal
            tiposPlatos={tiposPlatos}
            tagsCatalogo={tagsCatalogo}
            onClose={() => setShowNuevaRecetaModal(false)}
            onSubmit={(nuevaReceta) => {
              // Enviar el nombre del tipo de plato directamente
              // El backend validará que existe en la tabla tipos_plato
              const datosAPI = {
                nombre: nuevaReceta.nombre,
                descripcion: nuevaReceta.descripcion || '',
                tipo_plato: nuevaReceta.tipoPlato, // Enviar nombre del tipo de plato
                porciones: nuevaReceta.porciones || 1,
                tiempo_preparacion: 30, // Valor por defecto
                instrucciones: nuevaReceta.notasParaCocina || '',
                estado: 'borrador' as const,
                tags: (nuevaReceta.tags || []).map((t) => t.id),
              };

              console.log('🔧 Datos a enviar para crear receta:', datosAPI);
              crearReceta(datosAPI, {
                onSuccess: (nuevaReceta: any) => {
                  toastSuccess(
                    'Receta creada',
                    `"${nuevaReceta?.nombre || datosAPI.nombre}" se guardó correctamente.`
                  );
                  setShowNuevaRecetaModal(false);
                },
                onError: (err: any) => {
                  toastError(
                    'No se pudo crear la receta',
                    extraerMensajeError(err, 'Revisá los datos e intentá de nuevo.')
                  );
                  // Mantenemos el modal abierto para que el usuario corrija.
                },
              });
            }}
          />
        )}

        {/* Modal Detalles Receta */}
        {showDetallesModal && recetaSeleccionada && (
          <DetallesRecetaModal
            receta={
              // Buscar la receta más actualizada en la lista para reflejar cambios de React Query
              recetas.find((r) => r.id === recetaSeleccionada.id) ||
              recetaSeleccionada
            }
            onClose={cerrarModales}
          />
        )}

        {/* Modal Editar Receta */}
        {showEditarModal && recetaSeleccionada && (
          <EditarRecetaModal
            receta={
              // Buscar la receta más actualizada en la lista para reflejar cambios de React Query
              recetas.find((r) => r.id === recetaSeleccionada.id) ||
              recetaSeleccionada
            }
            onClose={cerrarModales}
            onSubmit={(recetaActualizada) => {
              // Enviar el nombre del tipo de plato directamente
              // El backend validará que existe en la tabla tipos_plato
              const datosAPI = {
                nombre: recetaActualizada.nombre,
                descripcion: recetaActualizada.descripcion || '',
                tipo_plato: recetaActualizada.tipoPlato, // Enviar nombre del tipo de plato
                porciones: recetaActualizada.porciones || 1,
                tiempo_preparacion: 30, // Default
                instrucciones: recetaActualizada.notasParaCocina || '',
                estado: recetaActualizada.estado || 'borrador',
                tags: (recetaActualizada.tags || []).map((t) => t.id),
              };

              console.log(
                '🔧 Datos a enviar para actualizar receta:',
                datosAPI
              );
              actualizarReceta(
                { id: recetaActualizada.id, data: datosAPI },
                {
                  onSuccess: () => {
                    toastSuccess(
                      'Receta actualizada',
                      `"${datosAPI.nombre}" se actualizó correctamente.`
                    );
                    cerrarModales();
                  },
                  onError: (err: any) => {
                    toastError(
                      'No se pudo actualizar la receta',
                      extraerMensajeError(err, 'Revisá los datos e intentá de nuevo.')
                    );
                  },
                }
              );
            }}
            ingredientes={ingredientes}
            tiposPlatos={tiposPlatos}
            tagsCatalogo={tagsCatalogo}
          />
        )}
      </div>

      {/* Fix tanda1 H3 (2026-06-30): confirmación antes de archivar receta */}
      <ConfirmModal
        isOpen={archivarConfirm.open}
        onClose={() =>
          setArchivarConfirm({ open: false, recetaId: '', recetaNombre: '' })
        }
        onConfirm={confirmarArchivarReceta}
        title="Archivar Receta"
        message={`¿Estás seguro de archivar "${archivarConfirm.recetaNombre}"? La receta dejará de aparecer en la lista de disponibles. Podrás restaurarla luego desde el filtro "Archivadas".`}
        confirmText="Archivar"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

// Componente Modal para Nueva Receta
interface NuevaRecetaModalProps {
  onClose: () => void;
  onSubmit: (receta: Receta) => void;
  tiposPlatos: TipoPlato[];
  tagsCatalogo: TagRecetaAPI[];
}

const NuevaRecetaModal: React.FC<NuevaRecetaModalProps> = ({
  onClose,
  onSubmit,
  tiposPlatos,
  tagsCatalogo,
}) => {
  const { user } = useAuth();
  // Default vacío: fuerza al usuario a elegir un tipo explícitamente. El
  // `required` del <select> bloquea el submit nativo si queda en ''.
  // (Antes era 'Normal' — valor que no existe en ningún catálogo y que el
  // backend rechazaba con 400 silencioso si el usuario no tocaba el dropdown.
  // Ver TD-023, salida 031.)
  const [formData, setFormData] = useState<NuevaRecetaFormData>({
    nombre: '',
    descripcion: '',
    tipoPlato: '',
    porciones: 10,
    notasParaCocina: '',
  });
  // Fix tanda1 H2 (2026-06-30): el form usaba sólo el `required` nativo
  // del browser → tooltip transitorio sin borde rojo persistente, el
  // usuario no veía qué faltaba. Ahora validamos en JS y mostramos
  // bordes rojos + mensajes inline.
  const [formErrors, setFormErrors] = useState<{ nombre?: string; tipoPlato?: string }>({});
  // Tags seleccionados como Set<id> para toggle eficiente.
  const [tagIdsSeleccionados, setTagIdsSeleccionados] = useState<Set<number>>(
    new Set()
  );
  const toggleTag = (id: number) => {
    setTagIdsSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Fix tanda1 H2: validación visible antes del submit.
    const errs: { nombre?: string; tipoPlato?: string } = {};
    if (!formData.nombre.trim()) errs.nombre = 'El nombre es requerido';
    if (!formData.tipoPlato) errs.tipoPlato = 'Seleccioná un tipo de plato';
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const tagsSeleccionadosObj = tagsCatalogo.filter((t) =>
      tagIdsSeleccionados.has(t.id)
    );

    const nuevaReceta: Receta = {
      id: Math.random().toString(36).substr(2, 9),
      nombre: formData.nombre,
      descripcion: formData.descripcion,
      // categoria: undefined, // Campo opcional, se omite
      tipoPlato: formData.tipoPlato,
      porciones: formData.porciones,
      estado: 'borrador',
      notasParaCocina: formData.notasParaCocina,
      tags: tagsSeleccionadosObj,
      ingredientes: [],
      fechaCreacion: new Date(),
      fechaActualizacion: new Date(),
      usuarioCreadorId: user?.id?.toString() || '1',
      usuarioCreador: {
        id: user?.id?.toString() || '1',
        nombre: user?.nombre || 'Usuario',
        email: user?.email || 'usuario@restaurante.cl',
      },
    };

    onSubmit(nuevaReceta);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <ChefHat className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Nueva Receta
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Crear nueva receta para el menú
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 p-6 space-y-6 overflow-y-auto"
        >
          {/* Información Básica */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Información Básica
            </h3>

            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre de la Receta *
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, nombre: e.target.value }));
                  if (formErrors.nombre) setFormErrors((p) => ({ ...p, nombre: undefined }));
                }}
                placeholder="Ej: Arroz con Pollo Tradicional"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                  formErrors.nombre
                    ? 'border-red-500 dark:border-red-400'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                required
              />
              {formErrors.nombre && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.nombre}</p>
              )}
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descripción
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    descripcion: e.target.value,
                  }))
                }
                placeholder="Breve descripción del plato..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                rows={3}
              />
            </div>

            {/* Tipo de Plato y Porciones */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tipo de Plato *
                </label>
                <select
                  value={formData.tipoPlato}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, tipoPlato: e.target.value }));
                    if (formErrors.tipoPlato) setFormErrors((p) => ({ ...p, tipoPlato: undefined }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                    formErrors.tipoPlato
                      ? 'border-red-500 dark:border-red-400'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  required
                >
                  <option value="" disabled>
                    Seleccioná un tipo
                  </option>
                  {tiposPlatos
                    .filter((tipo) => tipo.activo !== false)
                    .map((tipo) => (
                      <option key={tipo.id} value={tipo.nombre}>
                        {tipo.nombre}
                      </option>
                    ))}
                </select>
                {formErrors.tipoPlato && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.tipoPlato}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Porciones *
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.porciones}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      porciones: parseInt(e.target.value) || 10,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
            </div>

            {/* Notas para Cocina */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notas para Cocina
              </label>
              <textarea
                value={formData.notasParaCocina}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    notasParaCocina: e.target.value,
                  }))
                }
                placeholder="Instrucciones especiales, técnicas de cocción, puntos importantes..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                rows={4}
              />
            </div>

            {/* Tags (vegetariano, …). Multi-select por checkboxes. */}
            {tagsCatalogo.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Etiquetas
                </label>
                <div className="flex flex-wrap gap-2">
                  {tagsCatalogo.map((t) => {
                    const checked = tagIdsSeleccionados.has(t.id);
                    return (
                      <label
                        key={t.id}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border cursor-pointer transition-colors ${
                          checked
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-400 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={checked}
                          onChange={() => toggleTag(t.id)}
                        />
                        {t.nombre}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Información del Usuario */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <User className="h-4 w-4" />
              <span>Creado por: {user?.nombre || 'Usuario'}</span>
              <span>•</span>
              <span>Estado inicial: Borrador</span>
            </div>
          </div>

          {/* Nota informativa */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Package className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Gestión de Ingredientes
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Después de crear la receta, podrás agregar ingredientes y
                  gestionar el stock necesario desde la vista de edición.
                </p>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Save className="h-4 w-4 mr-2" />
            Crear Receta
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal para ver detalles de receta
interface DetallesRecetaModalProps {
  receta: Receta;
  onClose: () => void;
}

const DetallesRecetaModal: React.FC<DetallesRecetaModalProps> = ({
  receta,
  onClose,
}) => {
  // Obtener los datos completos de la receta incluyendo ingredientes
  const { data: recetaAPI } = useReceta(receta.id);
  const recetaCompleta = recetaAPI
    ? adaptarRecetaAPIToReceta(recetaAPI)
    : receta;

  const obtenerEstadoInfo = (estado: EstadoReceta) => {
    return ESTADOS_RECETA.find((e) => e.value === estado) || ESTADOS_RECETA[0];
  };

  const verificarStockSuficiente = (ingredientes: any[]) => {
    // Si no hay ingredientes disponibles, asumir que no tiene stock suficiente
    if (
      !ingredientes ||
      !Array.isArray(ingredientes) ||
      ingredientes.length === 0
    ) {
      // Fix tanda1 H5 (2026-06-30): si la receta no tiene ingredientes
      // cargados, antes devolvíamos `false` y la UI mostraba la falsa
      // alerta "Algunos ingredientes sin stock suficiente". Una receta
      // sin ingredientes no tiene stock que evaluar — devolvemos null
      // para que la UI ni muestre verde ni rojo, sino un mensaje
      // explícito "Receta sin ingredientes".
      return null;
    }
    return ingredientes.every((ing) => ing.stockSuficiente);
  };

  const estadoInfo = obtenerEstadoInfo(recetaCompleta.estado);
  const stockSuficiente = verificarStockSuficiente(
    recetaCompleta.ingredientes || []
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <ChefHat className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {recetaCompleta.nombre}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Detalles de la receta
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 p-6 space-y-6 overflow-y-auto">
          {/* Información General */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Información General
              </h3>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Descripción
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {recetaCompleta.descripcion || 'Sin descripción'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Tipo de Plato
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {recetaCompleta.tipoPlato || 'Sin tipo de plato'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Porciones
                    </label>
                    <div className="flex items-center text-sm text-gray-900 dark:text-white">
                      <Users className="h-4 w-4 mr-1" />
                      {recetaCompleta.porciones}
                    </div>
                  </div>
                </div>

                {/* Sección de Costos - Destacada */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/10 dark:to-blue-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                    <ShoppingCart className="h-5 w-5 mr-2 text-green-600 dark:text-green-400" />
                    Información de Costos
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Costo Total
                      </label>
                      <div className="flex items-center text-xl font-bold text-green-700 dark:text-green-400 mt-1">
                        {formatearMoneda(recetaCompleta.costoTotal || 0)}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Costo por Porción
                      </label>
                      <div className="flex items-center text-lg font-semibold text-gray-700 dark:text-gray-300 mt-1">
                        <Users className="h-4 w-4 mr-1" />
                        {recetaCompleta.porciones > 0
                          ? formatearMoneda(
                              (recetaCompleta.costoTotal || 0) /
                                recetaCompleta.porciones
                            )
                          : formatearMoneda(0)}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Estado
                  </label>
                  <div className="mt-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${estadoInfo.color}`}
                    >
                      {estadoInfo.label}
                    </span>
                  </div>
                </div>
              </div>

              {recetaCompleta.notasParaCocina && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-2">
                    Notas para Cocina
                  </h4>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      {recetaCompleta.notasParaCocina}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Estado del Stock
              </h3>

              {/* Fix tanda1 H5 (2026-06-30): si stockSuficiente es null
                  (receta sin ingredientes cargados), mostramos estado
                  neutral en gris en vez de la falsa alerta roja. */}
              <div
                className={`rounded-lg p-4 ${
                  stockSuficiente === null
                    ? 'bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700'
                    : stockSuficiente
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}
              >
                <div className="flex items-center">
                  {stockSuficiente === null ? (
                    <Package className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                  ) : stockSuficiente ? (
                    <Package className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
                  )}
                  <span
                    className={`font-medium ${
                      stockSuficiente === null
                        ? 'text-gray-700 dark:text-gray-300'
                        : stockSuficiente
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-red-800 dark:text-red-200'
                    }`}
                  >
                    {stockSuficiente === null
                      ? 'Receta sin ingredientes cargados'
                      : stockSuficiente
                      ? 'Todos los ingredientes disponibles'
                      : 'Algunos ingredientes sin stock suficiente'}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Información de Auditoría
                </h4>
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-1" />
                    <span>Creado por: {receta.usuarioCreador.nombre}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>
                      Creado: {receta.fechaCreacion.toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>
                      Actualizado:{' '}
                      {receta.fechaActualizacion.toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Ingredientes */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Ingredientes ({recetaCompleta.ingredientes.length})
            </h3>

            {recetaCompleta.ingredientes.length === 0 ? (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 text-center">
                <Package className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                  Sin ingredientes
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Esta receta aún no tiene ingredientes asignados
                </p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ingrediente
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Cantidad
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Costo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Stock
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Notas
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {recetaCompleta.ingredientes.map((ingrediente) => (
                      <tr key={ingrediente.id}>
                        <td className="px-4 py-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {ingrediente.ingrediente.nombre}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {ingrediente.ingrediente.categoria}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-900 dark:text-white">
                            {ingrediente.cantidad}{' '}
                            {getUnidadMedidaText(
                              ingrediente.ingrediente?.unidadMedida
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {formatearMoneda(ingrediente.costoLinea || 0)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatearMoneda(
                              ingrediente.ingrediente.costoUnitario || 0
                            )}{' '}
                            por{' '}
                            {getUnidadMedidaText(
                              ingrediente.ingrediente?.unidadMedida
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {ingrediente.stockSuficiente ? (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              {ingrediente.stockDisponible}{' '}
                              {getUnidadMedidaText(
                                ingrediente.ingrediente?.unidadMedida
                              )}
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                              {ingrediente.stockDisponible}{' '}
                              {getUnidadMedidaText(
                                ingrediente.ingrediente?.unidadMedida
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {ingrediente.notas || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal para editar receta - versión simplificada
interface EditarRecetaModalProps {
  receta: Receta;
  onClose: () => void;
  onSubmit: (receta: Receta) => void;
  ingredientes: Ingrediente[];
  tiposPlatos: TipoPlato[];
  tagsCatalogo: TagRecetaAPI[];
}

const EditarRecetaModal: React.FC<EditarRecetaModalProps> = ({
  receta,
  onClose,
  onSubmit,
  ingredientes,
  tiposPlatos,
  tagsCatalogo,
}) => {
  const { success: toastSuccess, error: toastError } = useToast();

  // Cargar los datos completos de la receta desde la API (incluye ingredientes)
  const { data: recetaCompletaAPI } = useReceta(receta.id);

  // Adaptar la receta completa de la API al formato del frontend
  const recetaCompleta = recetaCompletaAPI
    ? adaptarRecetaAPIToReceta(recetaCompletaAPI)
    : null;

  // Usar la receta completa si está disponible, sino usar la receta básica
  const recetaActual = recetaCompleta || receta;

  console.log('🔍 EditarRecetaModal - Receta actual:', {
    basica: receta.ingredientes?.length || 0,
    completa: recetaCompleta?.ingredientes?.length || 0,
    usando: recetaActual === recetaCompleta ? 'completa' : 'basica',
  });

  // Hooks para manejo de ingredientes en recetas
  const agregarIngredienteMutation = useAgregarIngrediente();
  const removerIngredienteMutation = useRemoverIngrediente();
  const actualizarIngredienteMutation = useActualizarIngrediente();
  const incrementarCantidadMutation = useIncrementarCantidadIngrediente();
  const crearIngredienteMutation = useCreateIngrediente();
  const reactivarIngredienteMutation = useReactivarIngrediente();

  const [formData, setFormData] = useState({
    nombre: recetaActual.nombre,
    descripcion: recetaActual.descripcion,
    // categoria: recetaActual.categoria, // Eliminado - ya no se usa
    // Si la receta no tiene tipoPlato, empezamos vacío. El required del select
    // fuerza al usuario a elegir uno válido antes de guardar. Antes era 'Normal',
    // valor inválido que el backend rechazaba con 400 (ver TD-023).
    tipoPlato: recetaActual.tipoPlato || '',
    porciones: recetaActual.porciones,
    notasParaCocina: recetaActual.notasParaCocina || '',
  });

  // Tags asignados a esta receta. Se inicializa con lo que viene de la API
  // (recetaActual.tags) y se actualiza al toggle de un checkbox.
  const [tagIdsSeleccionados, setTagIdsSeleccionados] = useState<Set<number>>(
    () => new Set((recetaActual.tags || []).map((t) => t.id))
  );
  // Re-sync al cargar la receta completa de la API (recetaCompleta llega async).
  useEffect(() => {
    setTagIdsSeleccionados(
      new Set((recetaActual.tags || []).map((t) => t.id))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recetaActual.id, (recetaActual.tags || []).length]);
  const toggleTag = (id: number) => {
    setTagIdsSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [busquedaIngrediente, setBusquedaIngrediente] = useState('');
  const [mostrarAgregarIngrediente, setMostrarAgregarIngrediente] =
    useState(false);
  const [mostrarCrearIngrediente, setMostrarCrearIngrediente] = useState(false);
  const [mostrarFormularioIngrediente, setMostrarFormularioIngrediente] =
    useState(false);
  const [nombreIngredienteBusqueda, setNombreIngredienteBusqueda] =
    useState('');

  // Estados para modal de error de duplicados
  const [errorModal, setErrorModal] = useState<{
    open: boolean;
    message: string;
    ingredientesSimilares: any[];
    ingredienteNuevo: any | null;
    ingredienteDataPendiente: any | null;
  }>({
    open: false,
    message: '',
    ingredientesSimilares: [],
    ingredienteNuevo: null,
    ingredienteDataPendiente: null,
  });

  // Usar directamente los ingredientes de la receta completa (incluye ingredientes desde la API)
  const ingredientesReceta = recetaActual.ingredientes || [];
  const [errors, setErrors] = useState<string[]>([]);

  // Estado local para las cantidades de ingredientes (para actualizaciones optimistas)
  const [cantidadesLocales, setCantidadesLocales] = useState<
    Record<string, number>
  >({});

  // Estado para cantidades temporales que se están editando (antes de confirmar)
  const [cantidadesTemporales, setCantidadesTemporales] = useState<
    Record<string, string>
  >({});

  // Estado para tracking de cuáles ingredientes tienen cambios pendientes
  const [ingredientesConCambios, setIngredientesConCambios] = useState<
    Set<string>
  >(new Set());

  // Estado para modal de confirmación de eliminación
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    ingredienteId: string;
    nombreIngrediente: string;
  }>({
    open: false,
    ingredienteId: '',
    nombreIngrediente: '',
  });

  // Estado para modal de ingredientes similares al crear
  const [ingredienteSimilarModal, setIngredienteSimilarModal] = useState<{
    open: boolean;
    message: string;
    ingredientesSimilares: any[];
    isExactoInactivo: boolean;
    ingredienteDataPendiente: any | null;
  }>({
    open: false,
    message: '',
    ingredientesSimilares: [],
    isExactoInactivo: false,
    ingredienteDataPendiente: null,
  });

  // Sincronizar cantidades locales con los datos de la receta cuando cambien
  React.useEffect(() => {
    const nuevasCantidades: Record<string, number> = {};
    ingredientesReceta.forEach((ing) => {
      nuevasCantidades[ing.ingredienteId] = ing.cantidad;
    });
    setCantidadesLocales(nuevasCantidades);
  }, [ingredientesReceta]);

  // Función para calcular el costo total de la receta
  const calcularCostoTotal = () => {
    let costoTotal = 0;

    console.log('🧮 Calculando costo total de la receta:', receta.nombre);

    ingredientesReceta.forEach((ingredienteReceta) => {
      const cantidadActual =
        cantidadesLocales[ingredienteReceta.ingredienteId] ??
        ingredienteReceta.cantidad;

      console.log('📊 Ingrediente:', {
        nombre: ingredienteReceta.ingrediente?.nombre,
        cantidad: cantidadActual,
        ingrediente: ingredienteReceta.ingrediente,
      });

      // Buscar el precio unitario del ingrediente
      let precioUnitario = 0;

      // Revisar si el ingrediente tiene costo unitario directo (desde la API)
      if (ingredienteReceta.ingrediente?.costoUnitario) {
        precioUnitario = ingredienteReceta.ingrediente.costoUnitario;
        console.log(
          '💰 Precio desde costoUnitario:',
          precioUnitario,
          'para',
          ingredienteReceta.ingrediente.nombre
        );
      }
      // Si no, buscar en proveedores como fallback
      else if (
        ingredienteReceta.ingrediente?.proveedores &&
        ingredienteReceta.ingrediente.proveedores.length > 0
      ) {
        const proveedorActivo = ingredienteReceta.ingrediente.proveedores.find(
          (p) => p.activo
        );
        if (proveedorActivo) {
          precioUnitario = proveedorActivo.precioUnitario;
          console.log(
            '💰 Precio desde proveedor:',
            precioUnitario,
            'para',
            ingredienteReceta.ingrediente.nombre
          );
        } else {
          console.log(
            '⚠️ No hay proveedores activos para:',
            ingredienteReceta.ingrediente.nombre
          );
        }
      } else {
        console.log(
          '❌ No hay precio disponible para:',
          ingredienteReceta.ingrediente?.nombre
        );
      }

      // Calcular costo parcial del ingrediente
      const costoParcial = cantidadActual * precioUnitario;
      costoTotal += costoParcial;

      console.log(
        '🧾 Costo parcial:',
        costoParcial,
        '(',
        cantidadActual,
        'x',
        precioUnitario,
        ')'
      );
    });

    console.log('💵 Costo total calculado:', costoTotal);
    return costoTotal;
  };

  const costoTotalReceta = calcularCostoTotal();

  // Funciones para gestionar ingredientes
  const ingredientesFiltrados = ingredientes.filter(
    (ing: Ingrediente) =>
      ing.nombre.toLowerCase().includes(busquedaIngrediente.toLowerCase()) &&
      !ingredientesReceta.some((ir) => ir.ingredienteId === ing.id)
  );

  const agregarIngrediente = (ingrediente: Ingrediente) => {
    console.log('🔥 Agregando ingrediente a receta:', {
      recetaId: receta.id,
      ingrediente: ingrediente.nombre,
    });

    // Preparar datos para la API
    const datosAPI = {
      ingrediente_id: parseInt(ingrediente.id),
      cantidad: 1, // Cantidad por defecto
      unidad_medida:
        typeof ingrediente.unidadMedida === 'string'
          ? ingrediente.unidadMedida
          : ingrediente.unidadMedida?.abreviacion || 'kg',
    };

    // Llamar a la API para agregar ingrediente a la receta
    agregarIngredienteMutation.mutate(
      {
        recetaId: receta.id,
        data: datosAPI,
      },
      {
        onSuccess: () => {
          setBusquedaIngrediente('');
          toastSuccess(
            `Ingrediente "${ingrediente.nombre}" agregado exitosamente`
          );
        },
        onError: (error: any) => {
          console.error('❌ Error al agregar ingrediente:', error);

          // Verificar si es error de ingredientes similares (409)
          if (error?.response?.status === 409) {
            const similares = error.response.data?.ingredientes_similares || [];
            const nuevo = error.response.data?.ingrediente_nuevo || null;

            setErrorModal({
              open: true,
              message:
                error.response.data?.mensaje ||
                'Se encontraron ingredientes similares en la receta',
              ingredientesSimilares: similares,
              ingredienteNuevo: nuevo,
              ingredienteDataPendiente: datosAPI,
            });
          } else {
            // Error normal
            toastError(
              error?.response?.data?.message ||
                'Error al agregar ingrediente. Intenta nuevamente.'
            );
          }
        },
      }
    );
  };

  const removerIngrediente = (ingredienteId: string) => {
    // Buscar el nombre del ingrediente para el mensaje de confirmación
    const ingredienteData = (recetaActual.ingredientes || []).find(
      (ing) => ing.id === ingredienteId
    );
    const nombreIngrediente =
      ingredienteData?.ingrediente.nombre || 'este ingrediente';

    // Abrir modal de confirmación
    setConfirmModal({
      open: true,
      ingredienteId,
      nombreIngrediente,
    });
  };

  const confirmarEliminarIngrediente = () => {
    console.log('🗑️ Removiendo ingrediente de receta:', {
      recetaId: receta.id,
      ingredienteId: confirmModal.ingredienteId,
    });

    // Llamar a la API para remover ingrediente de la receta
    removerIngredienteMutation.mutate(
      {
        recetaId: receta.id,
        ingredienteId: confirmModal.ingredienteId,
      },
      {
        onSuccess: () => {
          toastSuccess('Ingrediente eliminado exitosamente');
          console.log('✅ Ingrediente eliminado exitosamente');
          // Cerrar modal
          setConfirmModal({
            open: false,
            ingredienteId: '',
            nombreIngrediente: '',
          });
        },
        onError: (error: any) => {
          toastError('Error al eliminar ingrediente');
          console.error('❌ Error eliminando ingrediente:', error);
          // Cerrar modal
          setConfirmModal({
            open: false,
            ingredienteId: '',
            nombreIngrediente: '',
          });
        },
      }
    );
  };

  // Función para manejar cambios temporales en el input
  const manejarCambioTemporal = (ingredienteId: string, valor: string) => {
    setCantidadesTemporales((prev) => ({
      ...prev,
      [ingredienteId]: valor,
    }));

    // Marcar este ingrediente como que tiene cambios pendientes
    setIngredientesConCambios((prev) => new Set(prev).add(ingredienteId));
  };

  // Función para confirmar el cambio de cantidad
  const confirmarCantidadIngrediente = (ingredienteId: string) => {
    const valorTemporal = cantidadesTemporales[ingredienteId];
    if (valorTemporal === undefined || valorTemporal === '') return;

    const cantidad = parseFloat(valorTemporal);
    if (isNaN(cantidad) || cantidad < 0) {
      toastError('Ingresa una cantidad válida');
      return;
    }

    // Llamar a la función original de actualización
    actualizarCantidadIngrediente(ingredienteId, cantidad);

    // Limpiar el estado temporal y el tracking de cambios
    setCantidadesTemporales((prev) => {
      const nuevo = { ...prev };
      delete nuevo[ingredienteId];
      return nuevo;
    });

    setIngredientesConCambios((prev) => {
      const nuevo = new Set(prev);
      nuevo.delete(ingredienteId);
      return nuevo;
    });
  };

  // Función para cancelar cambios temporales
  const cancelarCambioTemporal = (ingredienteId: string) => {
    setCantidadesTemporales((prev) => {
      const nuevo = { ...prev };
      delete nuevo[ingredienteId];
      return nuevo;
    });

    setIngredientesConCambios((prev) => {
      const nuevo = new Set(prev);
      nuevo.delete(ingredienteId);
      return nuevo;
    });
  };

  const actualizarCantidadIngrediente = (
    ingredienteId: string,
    cantidad: number
  ) => {
    console.log('📝 Actualizando cantidad de ingrediente:', {
      recetaId: receta.id,
      ingredienteId,
      cantidad,
    });

    // Actualización optimista: actualizar el estado local inmediatamente
    setCantidadesLocales((prev) => ({
      ...prev,
      [ingredienteId]: cantidad,
    }));

    // Encontrar el ingrediente en la receta para obtener info adicional
    const ingredienteReceta = ingredientesReceta.find(
      (ir) => ir.ingredienteId === ingredienteId
    );
    if (!ingredienteReceta) {
      console.error('Ingrediente no encontrado en la receta');
      return;
    }

    // Preparar datos para la API
    const datosAPI: ActualizarIngredienteRequest = {
      cantidad: Math.max(0, cantidad),
      unidad_medida:
        typeof ingredienteReceta.unidadMedida === 'string'
          ? ingredienteReceta.unidadMedida
          : ingredienteReceta.unidadMedida?.abreviacion || 'kg',
    };

    // Llamar a la API para actualizar cantidad del ingrediente
    actualizarIngredienteMutation.mutate(
      {
        recetaId: receta.id,
        ingredienteId: ingredienteReceta.id, // Usar el ID de la relación, no del ingrediente
        data: datosAPI,
      },
      {
        onSuccess: () => {
          console.log('✅ Cantidad de ingrediente actualizada exitosamente');
          // La invalidación del cache se maneja en el hook useActualizarIngrediente
        },
        onError: (error: any) => {
          console.error(
            '❌ Error al actualizar cantidad de ingrediente:',
            error
          );
          // 422 = unidad incompatible con el tipo físico del ingrediente.
          // El backend devuelve un mensaje listo para mostrar al usuario.
          if (error?.response?.status === 422) {
            toastError(
              error.response.data?.message ??
                'Unidad incompatible con el tipo físico del ingrediente.'
            );
          } else {
            toastError('Error al actualizar la cantidad del ingrediente');
          }

          // Revertir cambio optimista en caso de error
          setCantidadesLocales((prev) => ({
            ...prev,
            [ingredienteId]: ingredienteReceta.cantidad,
          }));
        },
      }
    );
  };

  // Funciones para manejar el modal de error de duplicados
  const handleIncrementarCantidad = (
    ingredienteRecetaId: number,
    cantidadAdicional: number
  ) => {
    incrementarCantidadMutation.mutate(
      {
        recetaId: receta.id,
        ingredienteRecetaId,
        cantidadAdicional,
      },
      {
        onSuccess: () => {
          toastSuccess('Cantidad incrementada exitosamente');
          setErrorModal((prev) => ({ ...prev, open: false }));
        },
        onError: (error: any) => {
          console.error('❌ Error al incrementar cantidad:', error);
          toastError('Error al incrementar cantidad. Intenta nuevamente.');
        },
      }
    );
  };

  const handleAgregarDeTodasFormas = () => {
    if (!errorModal.ingredienteDataPendiente) return;

    // Agregar flag para forzar la adición
    const dataConFlag = {
      ...errorModal.ingredienteDataPendiente,
      forzarAdicion: true,
    };

    agregarIngredienteMutation.mutate(
      {
        recetaId: receta.id,
        data: dataConFlag,
      },
      {
        onSuccess: () => {
          setBusquedaIngrediente('');
          setErrorModal((prev) => ({ ...prev, open: false }));
          toastSuccess('Ingrediente agregado exitosamente');
        },
        onError: (error: any) => {
          console.error('❌ Error al forzar agregado:', error);
          toastError('Error al agregar ingrediente. Intenta nuevamente.');
        },
      }
    );
  };

  const handleCerrarModalError = () => {
    setErrorModal({
      open: false,
      message: '',
      ingredientesSimilares: [],
      ingredienteNuevo: null,
      ingredienteDataPendiente: null,
    });
  };

  // Funciones para manejar ingredientes similares/inactivos al crear ingredientes
  const handleForzarCreacionIngrediente = () => {
    if (!ingredienteSimilarModal.ingredienteDataPendiente) return;

    // Crear ingrediente forzando la creación
    crearIngredienteMutation.mutate(
      {
        ...ingredienteSimilarModal.ingredienteDataPendiente,
        forzarCreacion: true,
      } as any,
      {
        onSuccess: (ingredienteCreado) => {
          console.log(
            '✅ Ingrediente creado (forzado) exitosamente:',
            ingredienteCreado
          );

          // Agregar el ingrediente a la receta
          const datosAPI = {
            ingrediente_id: parseInt(ingredienteCreado.id),
            cantidad: 1,
            unidad_medida:
              typeof ingredienteCreado.unidadMedida === 'string'
                ? ingredienteCreado.unidadMedida
                : ingredienteCreado.unidadMedida?.abreviacion || 'kg',
          };

          agregarIngredienteMutation.mutate({
            recetaId: receta.id,
            data: datosAPI,
          });

          // Cerrar modales
          setMostrarCrearIngrediente(false);
          setMostrarFormularioIngrediente(false);
          setIngredienteSimilarModal({
            open: false,
            message: '',
            ingredientesSimilares: [],
            isExactoInactivo: false,
            ingredienteDataPendiente: null,
          });
          toastSuccess(
            `Ingrediente "${ingredienteCreado.nombre}" creado y agregado exitosamente`
          );
        },
        onError: (error: any) => {
          console.error('❌ Error al forzar creación de ingrediente:', error);
          toastError('Error al crear el ingrediente. Intenta nuevamente.');
        },
      }
    );
  };

  const handleReactivarIngredienteYAgregar = (ingredienteInactivo: any) => {
    // Primero reactivar el ingrediente inactivo
    console.log('🔄 Reactivando ingrediente:', ingredienteInactivo);

    // Reactivar con stock mínimo de 1 para poder usarlo
    reactivarIngredienteMutation.mutate(
      {
        id: parseInt(ingredienteInactivo.id),
        stockActual: 1, // Stock mínimo para reactivar
      },
      {
        onSuccess: (ingredienteReactivado) => {
          console.log('✅ Ingrediente reactivado:', ingredienteReactivado);

          // Ahora agregar el ingrediente reactivado a la receta
          const datosAPI = {
            ingrediente_id: parseInt(ingredienteReactivado.id),
            cantidad: 1,
            unidad_medida:
              ingredienteInactivo.unidad_medida?.abreviacion ||
              ingredienteInactivo.unidad_medida ||
              'kg',
          };

          agregarIngredienteMutation.mutate(
            {
              recetaId: receta.id,
              data: datosAPI,
            },
            {
              onSuccess: () => {
                // Cerrar modales
                setMostrarCrearIngrediente(false);
                setMostrarFormularioIngrediente(false);
                setIngredienteSimilarModal({
                  open: false,
                  message: '',
                  ingredientesSimilares: [],
                  isExactoInactivo: false,
                  ingredienteDataPendiente: null,
                });
                toastSuccess(
                  `Ingrediente "${ingredienteInactivo.nombre}" reactivado y agregado exitosamente`
                );
              },
              onError: (error: any) => {
                console.error(
                  '❌ Error al agregar ingrediente reactivado:',
                  error
                );
                toastError('Error al agregar el ingrediente a la receta.');
              },
            }
          );
        },
        onError: (error: any) => {
          console.error('❌ Error al reactivar ingrediente:', error);
          toastError('Error al reactivar el ingrediente. Intenta nuevamente.');
        },
      }
    );
  };

  // Funciones para manejar el modal de formulario de ingredientes
  const handleAbrirFormularioIngrediente = () => {
    setMostrarFormularioIngrediente(true);
  };

  const handleCerrarFormularioIngrediente = () => {
    setMostrarFormularioIngrediente(false);
  };

  const handleGuardarIngrediente = (ingredienteData: Partial<Ingrediente>) => {
    crearIngredienteMutation.mutate(ingredienteData, {
      onSuccess: (nuevoIngrediente) => {
        toastSuccess('Ingrediente creado exitosamente');
        setMostrarFormularioIngrediente(false);

        // Agregar automáticamente el ingrediente recién creado a la receta
        // Nota: Esta función se ejecuta en el contexto del EditarRecetaModal donde `receta` está disponible
        if (receta) {
          const datosIngrediente: AgregarIngredienteRequest = {
            ingrediente_id: parseInt(nuevoIngrediente.id),
            cantidad: 1, // Cantidad inicial por defecto
            unidad_medida: nuevoIngrediente.unidadMedida?.abreviacion || 'kg',
          };

          agregarIngredienteMutation.mutate(
            {
              recetaId: receta.id,
              data: datosIngrediente,
            },
            {
              onSuccess: () => {
                toastSuccess(
                  `Ingrediente "${nuevoIngrediente.nombre}" agregado a la receta`
                );
                // Cerrar el modal de agregar ingrediente si está abierto
                setMostrarAgregarIngrediente(false);
                setBusquedaIngrediente('');
              },
              onError: (error) => {
                console.error(
                  'Error al agregar ingrediente a la receta:',
                  error
                );
                toastError('Error al agregar el ingrediente a la receta');
              },
            }
          );
        }
      },
      onError: (error: any) => {
        console.error('Error al crear ingrediente:', error);

        // Manejo específico de errores de ingredientes similares/inactivos
        if (error?.response?.status === 409) {
          // Error de ingredientes similares
          const similares = error.response.data?.ingredientes_similares || [];
          setIngredienteSimilarModal({
            open: true,
            message:
              error.response.data?.error ||
              'Se encontraron ingredientes similares',
            ingredientesSimilares: similares,
            isExactoInactivo: false,
            ingredienteDataPendiente: ingredienteData,
          });
        } else if (error?.response?.status === 410) {
          // Error de ingrediente exacto inactivo
          const exacto = error.response.data?.ingrediente_exacto || null;
          setIngredienteSimilarModal({
            open: true,
            message:
              error.response.data?.error ||
              'Existe un ingrediente con el mismo nombre pero está inactivo',
            ingredientesSimilares: [exacto].filter(Boolean),
            isExactoInactivo: true,
            ingredienteDataPendiente: ingredienteData,
          });
        } else {
          // Error normal
          toastError('Error al crear el ingrediente');
        }
      },
    });
  };

  const crearYAgregarIngrediente = (ingredienteData: {
    nombre: string;
    descripcion: string;
    categoria: string;
    unidadMedidaId: string;
  }) => {
    console.log('🆕 Creando nuevo ingrediente desde receta:', ingredienteData);

    const datosParaCrear = {
      nombre: ingredienteData.nombre,
      descripcion:
        ingredienteData.descripcion ||
        `Ingrediente creado desde receta: ${ingredienteData.nombre}`,
      categoria: ingredienteData.categoria,
      unidadMedidaId: ingredienteData.unidadMedidaId,
      stockActual: 0,
      stockMinimo: 1,
      diasAlertaVencimiento: 7,
      activo: true,
      // No forzar creación inicialmente para permitir validaciones
      forzarCreacion: false,
    };

    console.log(
      '📋 Datos que se enviarán para crear ingrediente:',
      datosParaCrear
    );

    // Crear el ingrediente usando la API (sin forzar creación inicialmente)
    crearIngredienteMutation.mutate(datosParaCrear as any, {
      onSuccess: (ingredienteCreado) => {
        console.log('✅ Ingrediente creado exitosamente:', ingredienteCreado);
        console.log('📊 ID del ingrediente creado:', ingredienteCreado.id);
        console.log(
          '🏷️ Nombre del ingrediente creado:',
          ingredienteCreado.nombre
        );

        // Agregar el ingrediente a la receta
        const datosAPI = {
          ingrediente_id: parseInt(ingredienteCreado.id),
          cantidad: 1, // Cantidad por defecto
          unidad_medida:
            typeof ingredienteCreado.unidadMedida === 'string'
              ? ingredienteCreado.unidadMedida
              : ingredienteCreado.unidadMedida?.abreviacion || 'kg',
        };

        console.log(
          '📨 Agregando ingrediente a la receta con datos:',
          datosAPI
        );

        agregarIngredienteMutation.mutate({
          recetaId: receta.id,
          data: datosAPI,
        });

        // Cerrar el modal
        setMostrarCrearIngrediente(false);
        toastSuccess(
          `Ingrediente "${ingredienteCreado.nombre}" creado y agregado exitosamente. ID: ${ingredienteCreado.id}`
        );
      },
      onError: (error: any) => {
        console.error('❌ Error al crear ingrediente:', error);

        // Manejo específico de errores de ingredientes similares/inactivos
        if (error?.response?.status === 409) {
          // Error de ingredientes similares
          const similares = error.response.data?.ingredientes_similares || [];
          setIngredienteSimilarModal({
            open: true,
            message:
              error.response.data?.error ||
              'Se encontraron ingredientes similares',
            ingredientesSimilares: similares,
            isExactoInactivo: false,
            ingredienteDataPendiente: ingredienteData,
          });
        } else if (error?.response?.status === 410) {
          // Error de ingrediente exacto inactivo
          const exacto = error.response.data?.ingrediente_exacto || null;
          setIngredienteSimilarModal({
            open: true,
            message:
              error.response.data?.error ||
              'Existe un ingrediente con el mismo nombre pero está inactivo',
            ingredientesSimilares: [exacto].filter(Boolean),
            isExactoInactivo: true,
            ingredienteDataPendiente: ingredienteData,
          });
        } else {
          // Error normal
          toastError('Error al crear el ingrediente. Intenta nuevamente.');
        }
      },
    });
  };

  // Funciones para manejar categorías dinámicas - simplificadas
  // (ya no necesarias con IngredienteFormSimple)

  const publicarReceta = () => {
    if (!validarFormulario()) {
      return;
    }

    // Validaciones específicas para publicar
    if (ingredientesReceta.length === 0) {
      toastSuccess(
        'La receta debe tener al menos un ingrediente para ser publicada',
        undefined,
        4000
      );
      return;
    }

    const recetaPublicada: Receta = {
      ...receta,
      nombre: formData.nombre,
      descripcion: formData.descripcion,
      // categoria: undefined, // Eliminado - ya no se usa
      tipoPlato: formData.tipoPlato,
      porciones: formData.porciones,
      notasParaCocina: formData.notasParaCocina,
      ingredientes: ingredientesReceta,
      estado: 'disponible', // Cambiar estado a disponible
      fechaActualizacion: new Date(),
    };

    // El callback onSuccess/onError del padre (commit 6bd93ee) maneja
    // toast + cierre de modal según el resultado real de la mutation.
    // No disparamos toast verde optimista para evitar falsos positivos
    // (ver salida 034: si el backend rechaza, el verde quedaba arriba
    // del rojo).
    onSubmit(recetaPublicada);
  };

  const validarFormulario = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.nombre.trim()) {
      newErrors.push('El nombre de la receta es requerido');
    }

    if (formData.porciones <= 0) {
      newErrors.push('El número de porciones debe ser mayor a 0');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = () => {
    if (!validarFormulario()) {
      return;
    }

    const tagsSeleccionadosObj = tagsCatalogo.filter((t) =>
      tagIdsSeleccionados.has(t.id)
    );

    const recetaActualizada: Receta = {
      ...receta,
      nombre: formData.nombre,
      descripcion: formData.descripcion,
      // categoria: undefined, // Eliminado - ya no se usa
      tipoPlato: formData.tipoPlato,
      porciones: formData.porciones,
      notasParaCocina: formData.notasParaCocina,
      tags: tagsSeleccionadosObj,
      ingredientes: ingredientesReceta,
      fechaActualizacion: new Date(),
    };

    // Idem handlePublicarReceta: el callback del padre maneja UX
    // post-mutation. No toast verde optimista aquí (evita falsos
    // positivos cuando el backend rechaza — ver salida 034).
    onSubmit(recetaActualizada);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Editar Receta
            </h2>
            <div className="mt-1 flex items-center gap-4">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {receta.nombre}
              </span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">
                    Costo Total:
                  </span>
                  <span className="text-lg font-bold text-green-700 dark:text-green-300">
                    $
                    {costoTotalReceta.toLocaleString('es-CL', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                    Por porción:
                  </span>
                  <span className="text-lg font-bold text-blue-700 dark:text-blue-300">
                    $
                    {(costoTotalReceta / receta.porciones).toLocaleString(
                      'es-CL',
                      {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }
                    )}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({receta.porciones} porciones)
                  </span>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Errores */}
          {errors.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-400 mb-2">
                Errores en el formulario:
              </h3>
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Estado de la receta y publicación */}
          {receta.estado === 'borrador' && (
            <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-start space-x-3">
                <Package className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-400 mb-1">
                    Receta en Borrador
                  </h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                    Esta receta está en modo borrador. Para hacerla disponible
                    en el menú:
                  </p>
                  <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                    <li className="flex items-center">
                      <span
                        className={`inline-flex h-2 w-2 rounded-full mr-2 ${
                          formData.nombre.trim()
                            ? 'bg-green-500'
                            : 'bg-gray-400'
                        }`}
                      ></span>
                      Nombre de la receta{' '}
                      {formData.nombre.trim() ? '✓' : '(requerido)'}
                    </li>
                    <li className="flex items-center">
                      <span
                        className={`inline-flex h-2 w-2 rounded-full mr-2 ${
                          ingredientesReceta.length > 0
                            ? 'bg-green-500'
                            : 'bg-gray-400'
                        }`}
                      ></span>
                      Al menos un ingrediente{' '}
                      {ingredientesReceta.length > 0 ? '✓' : '(requerido)'}
                    </li>
                    <li className="flex items-center">
                      <span
                        className={`inline-flex h-2 w-2 rounded-full mr-2 ${
                          formData.porciones > 0
                            ? 'bg-green-500'
                            : 'bg-gray-400'
                        }`}
                      ></span>
                      Número de porciones{' '}
                      {formData.porciones > 0 ? '✓' : '(requerido)'}
                    </li>
                  </ul>
                  {formData.nombre.trim() &&
                    ingredientesReceta.length > 0 &&
                    formData.porciones > 0 && (
                      <div className="mt-2 flex items-center text-sm text-green-700 dark:text-green-300 font-medium">
                        <Package className="h-4 w-4 mr-1" />
                        ¡Lista para publicar! Usa el botón "Publicar Receta" al
                        final.
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-8">
            {/* FILA 1: Información básica */}
            <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6 flex items-center">
                <Edit className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                Información Básica de la Receta
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nombre de la receta *
                    </label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          nombre: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Ej: Pasta a la boloñesa"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tipo de Plato *
                    </label>
                    <select
                      value={formData.tipoPlato}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          tipoPlato: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    >
                      <option value="" disabled>
                        Seleccioná un tipo
                      </option>
                      {tiposPlatos
                        .filter((tipo) => tipo.activo !== false)
                        .map((tipo) => (
                          <option key={tipo.id} value={tipo.nombre}>
                            {tipo.nombre}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Porciones *
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.porciones}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            porciones: parseInt(e.target.value) || 1,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div></div> {/* Espacio vacío para mantener el layout */}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={formData.descripcion}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          descripcion: e.target.value,
                        }))
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Describe brevemente la receta..."
                    />
                  </div>
                </div>
              </div>

              {/* Notas para la cocina - A TODO EL ANCHO */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notas para la cocina
                </label>
                <textarea
                  value={formData.notasParaCocina}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      notasParaCocina: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Notas especiales para la preparación, técnicas de cocción, puntos importantes..."
                />
              </div>

              {/* Tags (vegetariano, …). Multi-select por checkboxes. */}
              {tagsCatalogo.length > 0 && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Etiquetas
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {tagsCatalogo.map((t) => {
                      const checked = tagIdsSeleccionados.has(t.id);
                      return (
                        <label
                          key={t.id}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border cursor-pointer transition-colors ${
                            checked
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-400 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="rounded"
                            checked={checked}
                            onChange={() => toggleTag(t.id)}
                          />
                          {t.nombre}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* FILA 2: Gestión de ingredientes */}
            <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                  <ShoppingCart className="h-5 w-5 mr-2 text-green-600 dark:text-green-400" />
                  Gestión de Ingredientes ({ingredientesReceta.length})
                </h3>
                <button
                  onClick={() =>
                    setMostrarAgregarIngrediente(!mostrarAgregarIngrediente)
                  }
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  <Plus className="h-4 w-4" />
                  <span>Agregar Ingrediente</span>
                </button>
              </div>

              {/* Estado de ingredientes - DEBAJO DEL TÍTULO */}
              {ingredientesReceta.length > 0 && (
                <div className="mb-6 p-3 rounded-lg border">
                  {(() => {
                    const ingredientesConStock = ingredientesReceta.filter(
                      (ing) =>
                        (ing.ingrediente?.stockActual || 0) >= ing.cantidad
                    );
                    const ingredientesStockBajo = ingredientesReceta.filter(
                      (ing) =>
                        (ing.ingrediente?.stockActual || 0) > 0 &&
                        (ing.ingrediente?.stockActual || 0) < ing.cantidad
                    );
                    const ingredientesSinStock = ingredientesReceta.filter(
                      (ing) => (ing.ingrediente?.stockActual || 0) === 0
                    );

                    const todoDisponible =
                      ingredientesConStock.length === ingredientesReceta.length;
                    const hayProblemas =
                      ingredientesStockBajo.length > 0 ||
                      ingredientesSinStock.length > 0;

                    return (
                      <div
                        className={`${
                          todoDisponible
                            ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                            : hayProblemas
                              ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
                              : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-sm font-medium ${
                              todoDisponible
                                ? 'text-green-800 dark:text-green-400'
                                : hayProblemas
                                  ? 'text-yellow-800 dark:text-yellow-400'
                                  : 'text-red-800 dark:text-red-400'
                            }`}
                          >
                            Estado de ingredientes:
                          </span>
                          <div className="flex items-center space-x-3 text-xs">
                            <span className="text-green-600 dark:text-green-400">
                              ✓ {ingredientesConStock.length} disponibles
                            </span>
                            {ingredientesStockBajo.length > 0 && (
                              <span className="text-yellow-600 dark:text-yellow-400">
                                ⚠ {ingredientesStockBajo.length} stock bajo
                              </span>
                            )}
                            {ingredientesSinStock.length > 0 && (
                              <span className="text-red-600 dark:text-red-400">
                                ✗ {ingredientesSinStock.length} sin stock
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Buscar ingredientes existentes */}
              {mostrarAgregarIngrediente && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <h4 className="text-sm font-medium text-green-800 dark:text-green-400 mb-3">
                    Buscar Ingredientes
                  </h4>
                  <input
                    type="text"
                    value={busquedaIngrediente}
                    onChange={(e) => setBusquedaIngrediente(e.target.value)}
                    placeholder="Buscar ingrediente para agregar..."
                    className="w-full px-3 py-2 border border-green-300 dark:border-green-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />

                  {busquedaIngrediente && ingredientesFiltrados.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto border border-green-300 dark:border-green-600 rounded-lg bg-white dark:bg-gray-700">
                      {ingredientesFiltrados
                        .slice(0, 5)
                        .map((ingrediente: Ingrediente) => (
                          <button
                            key={ingrediente.id}
                            onClick={() => agregarIngrediente(ingrediente)}
                            disabled={agregarIngredienteMutation.isPending}
                            className="w-full px-3 py-2 text-left hover:bg-green-50 dark:hover:bg-green-600/20 text-gray-900 dark:text-white text-sm border-b border-green-200 dark:border-green-600 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {agregarIngredienteMutation.isPending ? (
                              <span className="flex items-center">
                                <div className="animate-spin rounded-full h-3 w-3 border border-green-500 border-t-transparent mr-2"></div>
                                Agregando...
                              </span>
                            ) : (
                              <>
                                {ingrediente.nombre} (
                                {getUnidadMedidaText(ingrediente.unidadMedida)})
                              </>
                            )}
                          </button>
                        ))}
                    </div>
                  )}

                  {busquedaIngrediente &&
                    ingredientesFiltrados.length === 0 && (
                      <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 border border-green-300 dark:border-green-600 rounded-lg text-center">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          No se encontraron ingredientes con "
                          {busquedaIngrediente}"
                        </p>
                        <button
                          onClick={() => {
                            handleAbrirFormularioIngrediente();
                            setBusquedaIngrediente('');
                          }}
                          className="text-sm text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 font-medium"
                        >
                          + Crear "{busquedaIngrediente}" como nuevo ingrediente
                        </button>
                      </div>
                    )}

                  <div className="mt-3 flex justify-between">
                    <button
                      onClick={() => {
                        handleAbrirFormularioIngrediente();
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                    >
                      + Crear Nuevo
                    </button>
                    <button
                      onClick={() => setMostrarAgregarIngrediente(false)}
                      className="px-3 py-1 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 transition-colors text-sm"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              )}

              {/* Modal para crear nuevo ingrediente */}
              {mostrarCrearIngrediente && (
                <IngredienteFormSimple
                  onSave={crearYAgregarIngrediente}
                  onCancel={() => {
                    setMostrarCrearIngrediente(false);
                    setNombreIngredienteBusqueda('');
                  }}
                  loading={crearIngredienteMutation.isPending}
                  nombreInicial={nombreIngredienteBusqueda}
                />
              )}

              {/* Lista de ingredientes en la receta - CON COLUMNAS FIJAS */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ingredientes en la receta
                </h4>
                {ingredientesReceta.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    No hay ingredientes agregados
                  </p>
                ) : (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {/* Encabezados de columnas */}
                    <div className="grid grid-cols-12 gap-2 p-2 bg-gray-100 dark:bg-gray-600 rounded-lg text-xs font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-500">
                      <div className="col-span-3">NOMBRE</div>
                      <div className="col-span-2 text-center">STOCK</div>
                      <div className="col-span-2 text-center">CANTIDAD</div>
                      <div className="col-span-2 text-center">UNIDAD</div>
                      <div className="col-span-2 text-center">COSTO</div>
                      <div className="col-span-1 text-center">ACCIÓN</div>
                    </div>

                    {/* Lista de ingredientes */}
                    {ingredientesReceta.map((ingrediente) => (
                      <div
                        key={ingrediente.id}
                        className="grid grid-cols-12 gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg items-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      >
                        {/* NOMBRE */}
                        <div className="col-span-3">
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate block">
                            {ingrediente.ingrediente?.nombre || 'Ingrediente'}
                          </span>
                        </div>

                        {/* STOCK */}
                        <div className="col-span-2 text-center">
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${
                              (ingrediente.ingrediente?.stockActual || 0) >=
                              ingrediente.cantidad
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : (ingrediente.ingrediente?.stockActual || 0) >
                                    0
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}
                            title={`Stock disponible: ${ingrediente.ingrediente?.stockActual || 0} ${getUnidadMedidaText(
                              ingrediente.unidadMedida
                            )} | Necesario: ${ingrediente.cantidad} ${getUnidadMedidaText(
                              ingrediente.unidadMedida
                            )}`}
                          >
                            {(ingrediente.ingrediente?.stockActual || 0) >=
                            ingrediente.cantidad
                              ? '✓'
                              : (ingrediente.ingrediente?.stockActual || 0) > 0
                                ? '⚠'
                                : '✗'}
                            {ingrediente.ingrediente?.stockActual || 0}
                          </span>
                        </div>

                        {/* CANTIDAD */}
                        <div className="col-span-2 text-center">
                          {ingredientesConCambios.has(
                            ingrediente.ingredienteId
                          ) ? (
                            // Modo edición con botones de confirmación
                            <div className="flex flex-col gap-1">
                              <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={
                                  cantidadesTemporales[
                                    ingrediente.ingredienteId
                                  ] ?? ''
                                }
                                onChange={(e) =>
                                  manejarCambioTemporal(
                                    ingrediente.ingredienteId,
                                    e.target.value
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    confirmarCantidadIngrediente(
                                      ingrediente.ingredienteId
                                    );
                                  } else if (e.key === 'Escape') {
                                    cancelarCambioTemporal(
                                      ingrediente.ingredienteId
                                    );
                                  }
                                }}
                                disabled={
                                  actualizarIngredienteMutation.isPending ||
                                  removerIngredienteMutation.isPending
                                }
                                className="w-full px-2 py-1 border border-blue-300 dark:border-blue-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed text-center focus:ring-2 focus:ring-blue-500"
                                placeholder="Cantidad"
                                autoFocus
                              />
                              <div className="flex gap-1">
                                <button
                                  onClick={() =>
                                    confirmarCantidadIngrediente(
                                      ingrediente.ingredienteId
                                    )
                                  }
                                  disabled={
                                    !cantidadesTemporales[
                                      ingrediente.ingredienteId
                                    ] ||
                                    actualizarIngredienteMutation.isPending ||
                                    removerIngredienteMutation.isPending
                                  }
                                  className="flex-1 px-1 py-0.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  title="Confirmar cambio"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() =>
                                    cancelarCambioTemporal(
                                      ingrediente.ingredienteId
                                    )
                                  }
                                  disabled={
                                    actualizarIngredienteMutation.isPending ||
                                    removerIngredienteMutation.isPending
                                  }
                                  className="flex-1 px-1 py-0.5 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  title="Cancelar cambio"
                                >
                                  ✗
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Modo visualización - clickeable para editar
                            <button
                              onClick={() =>
                                manejarCambioTemporal(
                                  ingrediente.ingredienteId,
                                  (
                                    cantidadesLocales[
                                      ingrediente.ingredienteId
                                    ] ?? ingrediente.cantidad
                                  ).toString()
                                )
                              }
                              disabled={
                                actualizarIngredienteMutation.isPending ||
                                removerIngredienteMutation.isPending
                              }
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-center transition-colors"
                              title="Click para editar cantidad"
                            >
                              {cantidadesLocales[ingrediente.ingredienteId] ??
                                ingrediente.cantidad}
                            </button>
                          )}
                        </div>

                        {/* UNIDAD */}
                        <div className="col-span-2 text-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                            {getUnidadMedidaText(ingrediente.unidadMedida)}
                          </span>
                        </div>

                        {/* COSTO */}
                        <div className="col-span-2 text-center">
                          {(() => {
                            const cantidad =
                              cantidadesLocales[ingrediente.ingredienteId] ??
                              ingrediente.cantidad;
                            let precioUnitario = 0;

                            // Usar costoUnitario directamente desde el ingrediente
                            if (ingrediente.ingrediente?.costoUnitario) {
                              precioUnitario =
                                ingrediente.ingrediente.costoUnitario;
                            }
                            // Fallback a proveedores si no hay costoUnitario
                            else if (
                              ingrediente.ingrediente?.proveedores &&
                              ingrediente.ingrediente.proveedores.length > 0
                            ) {
                              const proveedorActivo =
                                ingrediente.ingrediente.proveedores.find(
                                  (p) => p.activo
                                );
                              if (proveedorActivo) {
                                precioUnitario = proveedorActivo.precioUnitario;
                              }
                            }

                            const costoParcial = cantidad * precioUnitario;

                            return (
                              <div className="text-xs">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  $
                                  {costoParcial.toLocaleString('es-CL', {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </div>
                                {precioUnitario > 0 && (
                                  <div className="text-gray-500 dark:text-gray-400">
                                    $
                                    {precioUnitario.toLocaleString('es-CL', {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}
                                    /u
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* ACCIÓN */}
                        <div className="col-span-1 text-center">
                          <button
                            onClick={() => removerIngrediente(ingrediente.id)}
                            disabled={
                              removerIngredienteMutation.isPending ||
                              actualizarIngredienteMutation.isPending
                            }
                            className="p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Eliminar ingrediente"
                          >
                            {removerIngredienteMutation.isPending ? (
                              <div className="animate-spin rounded-full h-4 w-4 border border-red-500 border-t-transparent"></div>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          {/* Estado actual */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Estado actual:
            </span>
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                receta.estado === 'borrador'
                  ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                  : receta.estado === 'disponible'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}
            >
              {receta.estado === 'borrador'
                ? 'Borrador'
                : receta.estado === 'disponible'
                  ? 'Disponible'
                  : 'Archivada'}
            </span>
          </div>

          {/* Botones */}
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Cancelar
            </button>

            {/* Botón Publicar - solo visible si está en borrador */}
            {receta.estado === 'borrador' && (
              <button
                onClick={publicarReceta}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <Package className="h-4 w-4 mr-2" />
                Publicar Receta
              </button>
            )}

            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Guardar Cambios
            </button>
          </div>
        </div>
      </div>

      {/* Modal de error para ingredientes duplicados */}
      <RecetaErrorModal
        isOpen={errorModal.open}
        onClose={handleCerrarModalError}
        message={errorModal.message}
        ingredientesSimilares={errorModal.ingredientesSimilares}
        ingredienteNuevo={errorModal.ingredienteNuevo}
        onIncrementarCantidad={handleIncrementarCantidad}
        onAgregarDeTodasFormas={handleAgregarDeTodasFormas}
      />

      {/* Modal del formulario de ingredientes reutilizado */}
      <IngredienteForm
        isOpen={mostrarFormularioIngrediente}
        onClose={handleCerrarFormularioIngrediente}
        onSave={handleGuardarIngrediente}
        ingrediente={null}
        modo="crear"
      />

      {/* Modal de confirmación para eliminar ingrediente */}
      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() =>
          setConfirmModal({
            open: false,
            ingredienteId: '',
            nombreIngrediente: '',
          })
        }
        onConfirm={confirmarEliminarIngrediente}
        title="Eliminar Ingrediente"
        message={`¿Estás seguro de eliminar "${confirmModal.nombreIngrediente}" de la receta? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
        loading={removerIngredienteMutation.isPending}
      />

      {/* Modal para ingredientes similares al crear */}
      <IngredienteSimilarModal
        isOpen={ingredienteSimilarModal.open}
        onClose={() =>
          setIngredienteSimilarModal({
            open: false,
            message: '',
            ingredientesSimilares: [],
            isExactoInactivo: false,
            ingredienteDataPendiente: null,
          })
        }
        message={ingredienteSimilarModal.message}
        ingredientesSimilares={ingredienteSimilarModal.ingredientesSimilares}
        isExactoInactivo={ingredienteSimilarModal.isExactoInactivo}
        onForzarCreacion={handleForzarCreacionIngrediente}
        onReactivarYAgregar={handleReactivarIngredienteYAgregar}
      />
    </div>
  );
};

export default Recetas;
